import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Input, TextField, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { NameOption } from '../../nameOptions';
import type { NameKind } from '../../namesDecisions';
import type { NameRowModel, PickerKey } from '../../useNamesStep';
import { NamePicker } from '../namePicker';
import { NameCandidates } from './NameCandidates';

type NameConsequenceProps = {
    kind: NameKind;
    row: NameRowModel;
    /** The catalogue's entities of this kind; empty until they load. */
    existing: NameOption[];
    /** Every category, for Nest under's parent. */
    parents: NameOption[];
    isCatalogLoading: boolean;
    onTargetChange: (key: PickerKey) => void;
    onNameChange: (name: string) => void;
    onParentChange: (key: PickerKey) => void;
};

// Literal keys per kind, so the locale guards see every one of them.
const CREATE_KEY: Record<NameKind, string> = {
    brands: 'catalogImport.names.create.brands',
    categories: 'catalogImport.names.create.categories',
    suppliers: 'catalogImport.names.create.suppliers',
};

const USE_KEY: Record<NameKind, string> = {
    brands: 'catalogImport.names.existing.brands',
    categories: 'catalogImport.names.existing.categories',
    suppliers: 'catalogImport.names.existing.suppliers',
};

const PICK_PLACEHOLDER_KEY: Record<NameKind, string> = {
    brands: 'catalogImport.names.useExisting.placeholder.brands',
    categories: 'catalogImport.names.useExisting.placeholder.categories',
    suppliers: 'catalogImport.names.useExisting.placeholder.suppliers',
};

const PICK_ARIA_KEY: Record<NameKind, string> = {
    brands: 'catalogImport.names.useExisting.aria.brands',
    categories: 'catalogImport.names.useExisting.aria.categories',
    suppliers: 'catalogImport.names.useExisting.aria.suppliers',
};

/**
 * What becomes of the name, under or beside the action control. An existing
 * name: "will use" with its entity. An ambiguous one: its candidates, one to
 * pick. A new one, by its action: "will create" with the text for Create; a
 * picker of the catalogue and of the file's other new texts for Use
 * existing; a text field for Rename; a parent picker for Nest under.
 */
export const NameConsequence: React.FC<NameConsequenceProps> = ({
    kind,
    row,
    existing,
    parents,
    isCatalogLoading,
    onTargetChange,
    onNameChange,
    onParentChange,
}) => {
    const { t } = useTranslation('pages');
    const emptyLabel = t('catalogImport.names.picker.empty');

    if (row.match === 'existing') {
        return (
            <div
                className="flex min-w-0 flex-wrap items-baseline gap-x-2"
                {...getNodeTestIdProp('catalog-import-names-existing')}
            >
                <Typography color="muted" type="body-sm">
                    {t(USE_KEY[kind])}
                </Typography>
                <Typography
                    className="min-w-0 break-words"
                    type="body-sm"
                    weight="medium"
                >
                    {row.existingLabel}
                </Typography>
            </div>
        );
    }

    if (row.match === 'ambiguous') {
        return (
            <NameCandidates
                candidates={row.candidates}
                onChange={onTargetChange}
                text={row.text}
                value={row.targetKey}
            />
        );
    }

    switch (row.draft.action) {
        case 'create':
            return (
                <div
                    className="flex min-w-0 flex-wrap items-baseline gap-x-2"
                    {...getNodeTestIdProp('catalog-import-names-create')}
                >
                    <Typography color="muted" type="body-sm">
                        {t(CREATE_KEY[kind])}
                    </Typography>
                    <Typography
                        className="min-w-0 break-words"
                        type="body-sm"
                        weight="medium"
                    >
                        {row.text}
                    </Typography>
                </div>
            );
        case 'useExisting':
            return (
                <NamePicker
                    ariaLabel={t(PICK_ARIA_KEY[kind], { text: row.text })}
                    emptyLabel={emptyLabel}
                    fileTextLabel={t('catalogImport.names.picker.file')}
                    isDisabled={
                        isCatalogLoading && row.fileOptions.length === 0
                    }
                    onChange={onTargetChange}
                    options={[...existing, ...row.fileOptions]}
                    placeholder={t(PICK_PLACEHOLDER_KEY[kind])}
                    value={row.targetKey}
                />
            );
        case 'rename':
            return (
                <div className="flex w-full flex-col gap-1">
                    <TextField
                        aria-label={t('catalogImport.names.rename.aria', {
                            text: row.text,
                        })}
                        className="w-full"
                        onChange={onNameChange}
                        value={row.draft.name}
                        {...getNodeTestIdProp('catalog-import-names-rename')}
                    >
                        <Input
                            placeholder={t(
                                'catalogImport.names.rename.placeholder',
                            )}
                        />
                    </TextField>
                    <Typography color="muted" type="body-xs">
                        {t('catalogImport.names.rename.consequence')}
                    </Typography>
                </div>
            );
        case 'nest':
            return (
                <div className="flex w-full flex-col gap-1">
                    <NamePicker
                        ariaLabel={t('catalogImport.names.nest.aria', {
                            text: row.text,
                        })}
                        emptyLabel={emptyLabel}
                        fileTextLabel={t('catalogImport.names.picker.file')}
                        isDisabled={isCatalogLoading}
                        onChange={onParentChange}
                        options={parents}
                        placeholder={t('catalogImport.names.nest.placeholder')}
                        value={row.draft.parentId}
                    />
                    <Typography color="muted" type="body-xs">
                        {t('catalogImport.names.nest.consequence', {
                            text: row.text,
                        })}
                    </Typography>
                </div>
            );
    }
};
